import React, { useMemo, useState, useEffect, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { FaPlus, FaEdit, FaTrash, FaShareAlt, FaCode, FaCopy, FaQrcode } from "react-icons/fa";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { useForms } from "../../contexts/FormsContext";
import DeleteButton from "../../components/UI/AlertDialog";
import Notification from "../../components/UI/Notification";
import { mapComponentFieldToAPIField } from '../../utils/formFieldMapping';
import Switch from "../../components/UI/Switch";
import QRCode from "react-qr-code";
import { AuthContext } from "../../contexts/AuthContext";
import { getRoleBasedRoute } from "../../utils/routeUtils";

const FormsList = () => {
    const { forms, createForm, updateForm, loading, error, deleteForm, fetchInternalForms, fetchExternalForms, internalForms, externalForms, internalFormsPagination, externalFormsPagination } = useForms();
    const { roles } = useContext(AuthContext);
    const navigate = useNavigate();
    const location = useLocation();
    const [notification, setNotification] = useState(null);
    const [tab, setTab] = useState('internal');
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState("");
    const [qrModalOpen, setQrModalOpen] = useState(false);
    const [selectedFormForQR, setSelectedFormForQR] = useState(null);

    // console.log("forms: ", forms)

    useEffect(() => {
        if (location.state?.message) {
            setNotification({
                message: location.state.message,
                type: "success",
            });
        }
        // If navigating from FormBuilder, set tab based on location.state.tab
        if (location.state?.tab) {
            setTab(location.state.tab);
            setCurrentPage(1);
            setSearchTerm("");
        }
        // Fetch forms for the current tab and page
        if (tab === 'internal') {
            fetchInternalForms(currentPage);
        } else {
            fetchExternalForms(currentPage);
        }
    }, [location, tab, currentPage]);

    const handleAddNewForm = () => {
        navigate(getRoleBasedRoute("/admin/formbuilder", roles));
    };

    const handleDelete = async (id) => {

    try {
        await deleteForm(id);
        // setNotification("Form deleted successfully");
        setNotification({
            message: "Form deleted successfully",
            type: "success",
        });
        
    } catch (error) {
        // setNotification("Failed to delete form");
        setNotification({
            message: "Failed to delete form",
            type: "success",
        });
    }
    };
    
    // set domain to the current window location
    const handleEmbedForm = (id, form_type) => {
        const domain = window.location.origin;
        const embedCode = `<iframe src="${domain}${form_type === "internal" ? getRoleBasedRoute("/admin/forms", roles) : "/forms_"}/${id}" width="600" height="400" frameborder="0"></iframe>`;
        navigator.clipboard.writeText(embedCode);
        // setNotification("Embed code copied to clipboard!");
        setNotification({
            message: "Embed code copied to clipboard!",
            type: "success",
        });
    };
    
    // set domain to the current window location
    const handleShareForm = (id, form_type) => {
        const domain = window.location.origin;
        const shareLink = `${domain}${form_type === "internal" ? getRoleBasedRoute("/admin/forms", roles) : "/forms_"}/${id}`;
        navigator.clipboard.writeText(shareLink);
        // setNotification("Shareable link copied to clipboard!");
        setNotification({
            message: "Shareable link copied to clipboard!",
            type: "success",
        });
    };
    
    // Duplicate form handler
    const handleDuplicateForm = async (form) => {
        try {
            const { id, version, created_by, updated_by, created_at, updated_at, slug, ...formData } = form;
            // Set new formName and formType, remove old name and FormType
            formData.formName = (form.name || form.formName) + " (Copy)";
            formData.formType = form.FormType || form.formType;
            delete formData.name;
            delete formData.FormType;
            // Map fields using the mapping function
            if (Array.isArray(formData.fields)) {
            formData.fields = formData.fields.map(mapComponentFieldToAPIField);
            }
            // Map fields using the mapping function if needed (assume already mapped)
            const newForm = await createForm(formData);
            // console.log("newForm: ", newForm)
            if (!newForm?.success) {
                setNotification({
                    message: newForm?.message || "Failed to duplicate form",
                    type: "error",
                });
                return;
            } else {
                setNotification({
                    message: newForm?.message || "Form duplicated successfully",
                    type: "success",
                });
            }

            if (newForm && newForm.id) {
                navigate(`${getRoleBasedRoute("/admin/formbuilder", roles)}/${newForm.id}`);
            }
        } catch (error) {
            setNotification({
                message: "Failed to duplicate form",
                type: "error",
            });
        }
    };
    
    // QR Code modal handlers
    const handleOpenQRModal = (form) => {
        setSelectedFormForQR(form);
        setQrModalOpen(true);
    };

    const handleCloseQRModal = () => {
        setQrModalOpen(false);
        setSelectedFormForQR(null);
    };

    const getQRCodeValue = (form) => {
        const domain = window.location.origin;
        return `${domain}${form.FormType === "internal" ? getRoleBasedRoute("/admin/forms", roles) : "/forms_"}/${form.id}`;
    };

    const handlePrintQR = () => {
        const printWindow = window.open('', '_blank');
        const qrValue = getQRCodeValue(selectedFormForQR);
        
        // Get the QR code SVG from the modal
        const qrModal = document.querySelector('.qr-modal-svg');
        const svgContent = qrModal ? qrModal.outerHTML : '';
        
        printWindow.document.write(`
            <!DOCTYPE html>
            <html>
            <head>
                <title>QR Code - ${selectedFormForQR.name}</title>
                <style>
                    body {
                        font-family: Arial, sans-serif;
                        margin: 0;
                        padding: 20px;
                        text-align: center;
                    }
                    .qr-container {
                        display: flex;
                        flex-direction: column;
                        align-items: center;
                        justify-content: center;
                        min-height: 100vh;
                    }
                    .qr-title {
                        font-size: 24px;
                        font-weight: bold;
                        margin-bottom: 20px;
                        color: #333;
                    }
                    .qr-code {
                        margin: 20px 0;
                        padding: 20px;
                        border: 2px solid #333;
                        display: inline-block;
                        background: white;
                    }
                    .qr-url {
                        font-size: 12px;
                        color: #666;
                        margin-top: 10px;
                        word-break: break-all;
                        max-width: 300px;
                    }
                    @media print {
                        body { margin: 0; }
                        .qr-container { min-height: auto; }
                        .qr-code { border: 2px solid #000; }
                    }
                </style>
            </head>
            <body>
                <div class="qr-container">
                    <div class="qr-title">${selectedFormForQR.name}</div>
                    <div class="qr-code">
                        ${svgContent}
                    </div>
                    <div class="qr-url">${qrValue}</div>
                </div>
            </body>
            </html>
        `);
        
        printWindow.document.close();
        printWindow.focus();
        printWindow.print();
        printWindow.close();
    };
    
    // Handle page change
    const handlePageChange = (page) => {
        setCurrentPage(page);
        if (tab === 'internal') {
            fetchInternalForms(page);
        } else {
            fetchExternalForms(page);
        }
    };

    const handleTabChange = (newTab) => {
        setTab(newTab);
        setCurrentPage(1);
        setSearchTerm("");
    };
    
    // Toggle form status handler
    const handleToggleStatus = async (form) => {
        const newStatus = form.status === "active" ? "inactive" : "active";
        // Optimistically update UI (optional)
        form.status = newStatus;
        const updateFormStatus = await updateForm(form.id, { status: newStatus });
        // console.log("status: ", updateFormStatus)
        if (updateFormStatus.success === true) {
            setNotification({ message: "Form status updated", type: "success"})
        } else {
            setNotification({ message: "Failed to update form status", type: "error"})
        }
        // Optionally, refetch forms or update state if needed
    };
    
    // Memoize the forms list rendering to optimize performance
    const formsList = tab === 'internal' ? internalForms : externalForms;
    const pagination = tab === 'internal' ? internalFormsPagination : externalFormsPagination;
    const filteredForms = useMemo(() => {
        if (!searchTerm) return formsList;
        const term = searchTerm.toLowerCase();
        return formsList.filter(form =>
            (form.name && form.name.toLowerCase().includes(term)) ||
            (form.purpose && form.purpose.toLowerCase().includes(term)) ||
            (form.FormType && form.FormType.toLowerCase().includes(term))
        );
    }, [formsList, searchTerm]);

    const renderedForms = useMemo(() => {
        return filteredForms.map((form) => (
            <div
                key={form.id}
                className="group relative p-6 bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 hover:border-blue-400 dark:hover:border-blue-500 transition-all duration-300 hover:shadow-2xl hover:shadow-blue-100 dark:hover:shadow-blue-900/20 transform hover:-translate-y-1 hover:scale-[1.02] backdrop-blur-sm"
            >

                {/* Content */}
                <div className="relative z-10">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">  
                            <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>

                            <h3 className="text-xl font-semibold text-gray-800 dark:text-white group-hover:text-blue-600 transition-all duration-300"
                                title={form.name}>
                                {form.name.length > 12 ? form.name.slice(0, 12) + "..." : form.name}
                            </h3>
                        </div> 
                        {/* Toggle Switch for Active/Inactive */}
                        <div className="z-20" title="Active/InActive form">
                            <Switch
                                checked={form.status === "active"}
                                onChange={() => handleToggleStatus(form)}
                                // label={form.status === "active" ? "active" : "inactive"}
                            />
                        </div> 
                    </div>
                    {/* <div>
                        <h1><span>{form.purpose}</span> - <span className="capitalize">{form.FormType}</span></h1>
                    </div> */}
                    {/* Action buttons - 5 circular buttons */}
                    <div className="flex items-center justify-center gap-2 sm:gap-3 mt-6 overflow-x-auto pb-2 px-2 pt-2">
                        {/* Duplicate button */}
                        <button
                            onClick={() => handleDuplicateForm(form)}
                            className="group/btn w-8 h-8 sm:w-10 sm:h-10 border border-blue-500 text-blue-600 hover:bg-blue-50 dark:border-blue-400 dark:text-blue-400 dark:hover:bg-blue-900/20 rounded-full shadow-lg hover:shadow-xl flex items-center justify-center transition-all duration-300 transform hover:scale-110 hover:-translate-y-1 flex-shrink-0"
                            title="Duplicate Form"
                        >
                            <FaCopy className="w-3 h-3 sm:w-4 sm:h-4" />
                        </button>
                        {/* Share button */}
                        <button
                            onClick={() => handleShareForm(form.id, form.FormType)}
                            className="group/btn w-8 h-8 sm:w-10 sm:h-10 border border-blue-500 text-blue-600 hover:bg-green-50 dark:border-green-400 dark:text-green-400 dark:hover:bg-green-900/20 rounded-full shadow-lg hover:shadow-xl flex items-center justify-center transition-all duration-300 transform hover:scale-110 hover:-translate-y-1 flex-shrink-0"
                            title="Share Form"
                        >
                            <FaShareAlt className="w-3 h-3 sm:w-4 sm:h-4 transition-transform duration-300 group-hover/btn:rotate-12" />
                        </button>
                        {/* Embed button */}
                        <button
                            onClick={() => handleEmbedForm(form.id, form.FormType)}
                            className="group/btn w-8 h-8 sm:w-10 sm:h-10 border border-blue-500 text-blue-600 hover:bg-blue-50 dark:border-blue-400 dark:text-blue-400 dark:hover:bg-blue-900/20 rounded-full shadow-lg hover:shadow-xl flex items-center justify-center transition-all duration-300 transform hover:scale-110 hover:-translate-y-1 flex-shrink-0"
                            title="Embed Form"
                        >
                            <FaCode className="w-3 h-3 sm:w-4 sm:h-4 transition-transform duration-300 group-hover/btn:-rotate-12" />
                        </button>
                        {/* QR Code button */}
                        <button
                            onClick={() => handleOpenQRModal(form)}
                            className="group/btn w-8 h-8 sm:w-10 sm:h-10 border border-blue-500 text-blue-600 hover:bg-blue-50 dark:border-blue-400 dark:text-orange-400 dark:hover:bg-blue-900/20 rounded-full shadow-lg hover:shadow-xl flex items-center justify-center transition-all duration-300 transform hover:scale-110 hover:-translate-y-1 flex-shrink-0"
                            title="QR Code"
                        >
                            <FaQrcode className="w-3 h-3 sm:w-4 sm:h-4 transition-transform duration-300 group-hover/btn:rotate-12" />
                        </button>
                        {/* Edit button */}
                        <button
                            onClick={() => navigate(`${getRoleBasedRoute("/admin/formbuilder", roles)}/${form.id}`)}
                            className="group/btn w-8 h-8 sm:w-10 sm:h-10 border border-blue-500 text-blue-600 hover:bg-blue-50 dark:border-blue-400 dark:text-blue-400 dark:hover:bg-blue-900/20 rounded-full shadow-lg hover:shadow-xl flex items-center justify-center transition-all duration-300 transform hover:scale-110 hover:-translate-y-1 flex-shrink-0"
                            title="Edit Form"
                        >
                            <FaEdit className="w-3 h-3 sm:w-4 sm:h-4 transition-transform duration-300 group-hover/btn:rotate-12" />
                        </button>
                        {/* Delete button */}
                        <DeleteButton
                            onDelete={() => handleDelete(form.id)}
                            style="group/btn w-8 h-8 sm:w-10 sm:h-10 border border-red-500 text-red-600 hover:bg-red-50 dark:border-red-400 dark:text-red-400 dark:hover:bg-red-900/20 rounded-full shadow-lg hover:shadow-xl flex items-center justify-center transition-all duration-300 transform hover:scale-110 hover:-translate-y-1 flex-shrink-0"
                            title="Delete Form"
                            delete_icon={<FaTrash className="w-3 h-3 sm:w-4 sm:h-4 transition-transform duration-300 group-hover/btn:scale-110" />}
                        />
                    </div>
                </div>
            </div>
        ));
    }, [formsList, navigate, handleDelete]);

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-purple-50/30 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800 p-6">
                <div className="max-w-7xl mx-auto">
                    <div className="flex justify-between items-center mb-8">
                        <div className="space-y-2">
                            <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-700 via-gray-600 to-gray-500 bg-clip-text text-transparent">My Forms</h1>
                            <div className="w-16 h-1 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full"></div>
                        </div>
                        <button
                            className="px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-700 text-white rounded-xl shadow-lg flex items-center gap-3 font-medium opacity-50 cursor-not-allowed"
                            disabled>
                            <FaPlus className="text-lg" /> Add New Form
                        </button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {Array.from({ length: 6 }).map((_, index) => (
                            <div key={index} className="p-6 bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700">
                                <Skeleton 
                                    height={120} 
                                    baseColor={localStorage.getItem("theme") === "dark" ? '#374151' : '#e0e0e0'}
                                    highlightColor={localStorage.getItem("theme") === "dark" ? '#4b5563' : '#f5f5f5'}
                                    containerClassName={localStorage.getItem("theme") === "dark" ? 'bg-gray-800' : ''}
                                    className={localStorage.getItem("theme") === "dark" ? 'bg-gray-700' : ''}
                                />
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-50 via-red-50/30 to-pink-50/30 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800 p-6 flex items-center justify-center">
                <div className="text-center p-8 bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-red-200 dark:border-red-800">
                    <div className="w-16 h-16 bg-gradient-to-r from-red-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-4">
                        <span className="text-white text-2xl">!</span>
                    </div>
                    <p className="text-red-600 dark:text-red-400 text-lg font-medium">Error: {error}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-purple-50/30 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800 px-6 py-8 font-poppins">
            <div className=" mx-auto">
                
                {/* notification compo */}
                {notification && (
                    <div className="fixed top-4 right-4 z-50">
                        <Notification message={notification.message} type={notification.type} onClose={() => setNotification(null)} />
                    </div>
                )}

                {/* Header Section */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6 mb-8">
                    <div className="space-y-3">
                        <h1 className="text-4xl font-semibold bg-gradient-to-r from-gray-800 via-gray-700 to-gray-600 dark:from-white dark:via-gray-100 dark:to-gray-200 bg-clip-text text-transparent">
                            My Forms
                        </h1>
                        {/* <div className="w-20 h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 rounded-full"></div> */}
                        <p className="text-gray-600 dark:text-gray-400 text-lg">Manage and organize your forms</p>
                    </div>
                    <button
                        onClick={handleAddNewForm}
                        className="group flex items-center gap-3 bg-blue-700 text-white px-8 py-4 rounded-2xl w-full sm:w-auto font-semibold shadow-2xl"
                    >
                        <FaPlus className="text-xl transition-transform duration-300 group-hover:rotate-180" />
                        <span>Add New Form</span>
                    </button>
                </div>

                {/* Tabs */}
                <div className="mb-6 flex gap-4">
                    <button
                        className={`px-6 py-2 rounded-t-lg font-semibold border-b-2 transition-colors duration-200 ${tab === 'internal' ? 'border-blue-600 text-blue-600 bg-white dark:bg-gray-900' : 'border-transparent text-gray-500 bg-gray-100 dark:bg-gray-800'}`}
                        onClick={() => handleTabChange('internal')}
                    >
                        Internal Forms
                    </button>
                    <button
                        className={`px-6 py-2 rounded-t-lg font-semibold border-b-2 transition-colors duration-200 ${tab === 'external' ? 'border-blue-600 text-blue-600 bg-white dark:bg-gray-900' : 'border-transparent text-gray-500 bg-gray-100 dark:bg-gray-800'}`}
                        onClick={() => handleTabChange('external')}
                    >
                        External Forms
                    </button>
                </div>

                {/* Search Bar */}
                <div className="mb-8 flex justify-start">
                    <input
                        type="text"
                        className="border rounded px-4 py-2 text-sm w-full max-w-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Search forms..."
                        value={searchTerm}
                        onChange={e => {
                            setSearchTerm(e.target.value);
                            setCurrentPage(1);
                        }}
                    />
                </div>

                {/* Forms Grid */}
                {filteredForms.length === 0 ? (
                    <div className="text-center py-20">
                        <div className="w-24 h-24 bg-gradient-to-r from-gray-300 to-gray-400 dark:from-gray-600 dark:to-gray-700 rounded-full flex items-center justify-center mx-auto mb-6">
                            <FaPlus className="text-3xl text-white" />
                        </div>
                        <p className="text-gray-500 dark:text-gray-400 text-xl font-medium mb-4">No forms available yet</p>
                        <p className="text-gray-400 dark:text-gray-500">Click "Add New Form" to create your first form</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {renderedForms}
                    </div>
                )}
                {/* Pagination Controls */}
                {(!searchTerm && pagination?.meta && pagination.meta.last_page > 1 && filteredForms.length > 0) && (
                    <div className="flex justify-center mt-8 gap-2">
                        <button
                            className="px-4 py-2 rounded bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-white font-medium disabled:opacity-50"
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage <= 1}
                        >
                            Prev
                        </button>
                        {Array.from({ length: pagination.meta.last_page }, (_, i) => i + 1).map(page => (
                            <button
                                key={page}
                                className={`px-4 py-2 rounded font-medium ${page === currentPage ? 'bg-blue-600 text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-white'}`}
                                onClick={() => handlePageChange(page)}
                            >
                                {page}
                            </button>
                        ))}
                        <button
                            className="px-4 py-2 rounded bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-white font-medium disabled:opacity-50"
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage >= pagination.meta.last_page}
                        >
                            Next
                        </button>
                    </div>
                )}

                {/* QR Code Modal */}
                {qrModalOpen && selectedFormForQR && (
                    <div className="fixed inset-0 z-20 bg-black/50 backdrop-blur-sm flex items-center justify-center">
                        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4 transform transition-all">
                            <div className="flex items-center justify-between mb-6">
                                <h3 className="text-xl font-semibold text-gray-800 dark:text-white">
                                    QR Code for {selectedFormForQR.name}
                                </h3>
                                <button
                                    onClick={handleCloseQRModal}
                                    className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
                                >
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                            
                            <div className="flex flex-col items-center space-y-4">
                                                            <div className="bg-white p-4 rounded-lg border-2 border-gray-200">
                                <div className="qr-modal-svg">
                                    <QRCode 
                                        value={getQRCodeValue(selectedFormForQR)}
                                        size={200}
                                        level="M"
                                        bgColor="white"
                                        fgColor="black"
                                    />
                                </div>
                            </div>
                                
                                <div className="text-center">
                                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                                        Scan this QR code to access the form
                                    </p>
                                    <p className="text-xs text-gray-500 dark:text-gray-500 break-all">
                                        {getQRCodeValue(selectedFormForQR)}
                                    </p>
                                </div>
                                
                                <div className="flex gap-3">
                                    <button
                                        onClick={() => {
                                            navigator.clipboard.writeText(getQRCodeValue(selectedFormForQR));
                                            setNotification({
                                                message: "Form URL copied to clipboard!",
                                                type: "success",
                                            });
                                        }}
                                        className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors text-sm font-medium"
                                    >
                                        Copy URL
                                    </button>
                                    <button
                                        onClick={handlePrintQR}
                                        className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors text-sm font-medium flex items-center gap-2"
                                    >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                                        </svg>
                                        Print QR
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default React.memo(FormsList);